import { dehydrate, QueryClient } from '@tanstack/react-query';
import { getLocaleCodeByCountryCode, SUPPORTED_LOCALES_OBJ, type SupportedLocale } from '@zep-us/i18n';
import Axios from 'axios';
import { type GetServerSidePropsContext } from 'next';
import { ZepQuizApiError } from '@/api/httpClient';
import { useQuizDetail } from '@/api/quiz';
import { withSsrLogging } from '@/shared/logger/withSsrLogging';
import { logGooglebotDebug } from '@/utils/common';
import { getDefaultServerSideProps } from '@/utils/next';
import { handleIfModifiedSince, setNotFoundPageCache, setPublicPageCache, setRedirectPageCache } from '@/utils/ssr';
import { isQuizDetailIndexable } from './utils/isQuizDetailIndexable';
import { makeQuizDetailSeoInfo } from './utils/makeQuizDetailSeoInfo';
import { QuizDetailPageView } from './QuizDetailPageView';

// 백엔드가 4xx/non-SUCCESS 응답 시:
//  - response.data.status 가 있으면 → ZepQuizApiError
//  - 없으면 → raw AxiosError
// 둘 다 httpStatus/response.status 를 검사해서 404 만 캐시 가능한 not-found 로
// 변환한다. 그 외 장애(5xx/timeout/네트워크)는 rethrow → Next.js 500.
const isExplicitNotFound = (e: unknown): boolean => {
  if (e instanceof ZepQuizApiError) {
    return e.httpStatus === 404 || e.status === 'FAILED';
  }
  if (Axios.isAxiosError(e)) {
    return e.response?.status === 404;
  }
  return false;
};
export default QuizDetailPageView;

/**
 * UA 별 Last-Modified 계산.
 *  - Googlebot: `updatedAt` 만. 배포와 무관하게 콘텐츠 변경 없으면 304 유지.
 *  - 일반 유저: `max(updatedAt, BUILD_TIMESTAMP)`. 배포마다 자연 bump 되어
 *    브라우저가 새 HTML 을 받아 깜빡임을 방지한다.
 *
 * ETag 는 `next.config.js` 의 `generateEtags: false` 로 끄고 Last-Modified 만
 * 쓴다. body-hash ETag 는 번들 변경 같은 콘텐츠 외적 변화에도 bump 되어
 * Googlebot 의 ETag 우선 평가를 통해 우리 의도한 304 경로를 막기 때문.
 */
const computeQuizLastModified = ({
  isBot,
  updatedAt
}: {
  isBot: boolean;
  updatedAt: string | number | Date | null | undefined;
}): Date => {
  const contentTime = new Date(updatedAt ?? 0);
  if (isBot) return contentTime;
  const buildTime = new Date(Number(process.env.BUILD_TIMESTAMP) || 0);
  return new Date(Math.max(contentTime.getTime(), buildTime.getTime()));
};
const logGooglebotQuizSsrDebug = ({
  context,
  event,
  quizId,
  locale,
  mappedLocale,
  statusCode,
  extra
}: {
  context: GetServerSidePropsContext;
  event: 'canonical-redirect' | 'conditional-result';
  quizId: string;
  locale: string;
  mappedLocale: string;
  statusCode: number;
  extra?: Record<string, unknown>;
}) => {
  if (context.req.headers['x-client-type'] !== 'bot') return;
  logGooglebotDebug({
    event,
    runtime: 'nodejs',
    routeKind: 'quiz-detail',
    quizId,
    locale,
    mappedLocale,
    requestUrl: context.req.url,
    statusCode,
    referer: context.req.headers.referer ?? null,
    'if-modified-since': context.req.headers['if-modified-since'] ?? null,
    'if-none-match': context.req.headers['if-none-match'] ?? null,
    'user-agent': context.req.headers['user-agent'] ?? null,
    ...extra
  });
};
export const getServerSideProps = withSsrLogging('quiz-detail', async function getServerSidePropsInner(context: GetServerSidePropsContext) {
  const {
    locale = SUPPORTED_LOCALES_OBJ.en,
    params
  } = context;
  const queryClient = new QueryClient();
  const quizId = params?.id as string ?? '';

  // prefetchQuery 는 silent fail (undefined) → 백엔드 장애가 그대로 false 404
  // 로 엣지에 캐시되는 사고가 발생. fetchQuery 로 바꾸되, throw 된 에러를
  // "명시적 not-found" 와 "그 외 장애" 로 구분한다.
  //   - 명시적 not-found(404/FAILED) → setNotFoundPageCache + notFound:true (캐시 OK)
  //   - 그 외 장애(5xx/timeout/네트워크) → rethrow → Next.js 500 (캐시 안 됨)
  const handleNotFound = () => {
    const isBot = context.req.headers['x-client-type'] === 'bot';
    setNotFoundPageCache(context, {
      profile: isBot ? 'bot' : 'user'
    });
    return {
      notFound: true as const
    };
  };

  // 두 호출의 throw 처리를 분리한다.
  //   - quizDetail throw → not-found vs 장애 분기
  //   - getDefaultServerSideProps throw → 그대로 rethrow (i18n/서버 장애)
  // 여기서 로그를 찍지 않는다 — rethrow 는 바깥 withSsrLogging 이 err 째로
  // 기록하며, 중복으로 찍으면 장애 1건이 2건으로 집계된다.
  const quizFetch = queryClient.fetchQuery(useQuizDetail.getFetchOptions({
    id: quizId
  })).catch((e: unknown) => {
    if (isExplicitNotFound(e)) return {
      __notFound: true
    } as const;
    throw e;
  });
  const [{
    props: defaultProps
  }, quizDetailOrNotFound] = await Promise.all([getDefaultServerSideProps(context), quizFetch]);
  if (quizDetailOrNotFound && '__notFound' in quizDetailOrNotFound && quizDetailOrNotFound.__notFound) {
    return handleNotFound();
  }
  const quizDetail = quizDetailOrNotFound as Exclude<typeof quizDetailOrNotFound, {
    __notFound: true;
  }>;

  // 200 OK 라도 응답 body 의 status 가 FAILED 면 not-found 처리. (httpClient
  // interceptor 가 잡지 못한 케이스 대비 안전망)
  if (quizDetail?.status === 'FAILED') return handleNotFound();
  const mappedLocale = getLocaleCodeByCountryCode(quizDetail.data.countryCode);
  const canonicalUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${mappedLocale === 'ko' ? '' : `/${mappedLocale}`}/quiz/${quizId}`;
  const ogUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${locale === 'ko' ? '' : `/${locale}`}/quiz/${quizId}`;
  const seoContent = makeQuizDetailSeoInfo(quizDetail.data, locale as SupportedLocale, defaultProps._nextI18Next, canonicalUrl);
  const isBot = context.req.headers['x-client-type'] === 'bot';
  const shouldRedirect = isBot && locale !== mappedLocale;
  if (shouldRedirect) {
    logGooglebotQuizSsrDebug({
      context,
      event: 'canonical-redirect',
      quizId,
      locale,
      mappedLocale,
      statusCode: 301,
      extra: {
        destination: canonicalUrl,
        quizUpdatedAt: quizDetail.data.updatedAt,
        isPublic: quizDetail.data.isPublic
      }
    });

    // 301 도 Edge 캐싱. 봇이 같은 잘못된 locale URL 반복 접속 시 동일 redirect
    // 를 재계산하지 않고, 백엔드 quizDetail 조회까지 우회한다.
    setRedirectPageCache(context);
    return {
      redirect: {
        destination: canonicalUrl,
        statusCode: 301
      }
    };
  }

  // 공개 퀴즈만 Edge 캐싱 (비공개는 no-store)
  setPublicPageCache(context, {
    cacheable: quizDetail.data.isPublic,
    profile: isBot ? 'bot' : 'user'
  });

  // UA 별 엣지 캐시 버킷 분리. middleware 가 x-client-type(bot|user) 을 주입한다.
  // Vary 덕분에 엣지는 bot/user 두 버킷으로만 응답을 저장해 효율을 유지한다.
  context.res.setHeader('Vary', 'x-client-type');
  const lastModified = computeQuizLastModified({
    isBot,
    updatedAt: quizDetail.data.updatedAt
  });
  const conditionalMatched = handleIfModifiedSince(context, lastModified);
  logGooglebotQuizSsrDebug({
    context,
    event: 'conditional-result',
    quizId,
    locale,
    mappedLocale,
    statusCode: conditionalMatched ? 304 : 200,
    extra: {
      conditionalMatched,
      cacheProfile: isBot ? 'bot' : 'user',
      cacheControl: context.res.getHeader('Cache-Control') ?? null,
      vary: context.res.getHeader('Vary') ?? null,
      lastModified: context.res.getHeader('Last-Modified') ?? null,
      quizUpdatedAt: quizDetail.data.updatedAt,
      isPublic: quizDetail.data.isPublic
    }
  });
  if (conditionalMatched) {
    return {
      props: {} as never
    };
  }
  return {
    props: {
      ...defaultProps,
      quizId,
      dehydratedState: dehydrate(queryClient),
      seoContent,
      noIndex: !isQuizDetailIndexable(quizDetail.data),
      canonicalUrl,
      ogUrl
    }
  };
});